import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Sparkles, MapPin, Bed, Landmark, Star, Map as MapIcon, Moon, Route } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useSiteImage } from "@/contexts/SiteImagesContext";
import PlaceCard from "@/components/PlaceCard";

const TYPE_ICONS = {
  hospitality: <Bed className="w-6 h-6" />,
  museum: <Landmark className="w-6 h-6" />,
  attraction: <Star className="w-6 h-6" />,
  neighborhood: <MapIcon className="w-6 h-6" />,
  nightlife: <Moon className="w-6 h-6" />,
  route: <Route className="w-6 h-6" />,
};

export default function Home() {
  const { t, tx } = useI18n();
  const heroImg = useSiteImage("hero_home", "https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1920&q=80");
  const hospitalityImg = useSiteImage("category_hospitality", "");
  const museumImg = useSiteImage("category_museum", "");
  const attractionImg = useSiteImage("category_attraction", "");
  const neighborhoodImg = useSiteImage("category_neighborhood", "");
  const nightlifeImg = useSiteImage("category_nightlife", "");
  const routeImg = useSiteImage("category_route", "");
  const mapPreviewImg = useSiteImage("home_map_preview", "https://images.unsplash.com/photo-1524661135-423995f22d0b?w=1200");

  const typeImageMap = {
    hospitality: hospitalityImg, museum: museumImg, attraction: attractionImg,
    neighborhood: neighborhoodImg, nightlife: nightlifeImg, route: routeImg,
  };

  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/places", { params: { featured: true, limit: 6 } }).then(r => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[88vh] min-h-[560px] overflow-hidden">
          <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 hero-scrim" />
          <div className="relative h-full max-w-7xl mx-auto px-5 lg:px-8 flex flex-col justify-end pb-16 md:pb-24">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--primary-red))]/95 text-white text-xs font-semibold tracking-wide uppercase animate-fade-up" data-testid="hero-kicker">
                <Sparkles className="w-3.5 h-3.5" /> {t.hero.kicker}
              </span>
              <h1 className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight text-white animate-fade-up" style={{ animationDelay: "60ms" }}>
                {t.hero.title}
              </h1>
              <p className="mt-5 text-base md:text-lg text-white/90 max-w-2xl animate-fade-up" style={{ animationDelay: "120ms" }}>
                {t.hero.subtitle}
              </p>

              <div className="mt-8 flex flex-col md:flex-row gap-3 max-w-2xl animate-fade-up" style={{ animationDelay: "180ms" }}>
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={t.hero.searchPh}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") window.location.href = `/places?q=${encodeURIComponent(search)}`; }}
                    data-testid="hero-search-input"
                    className="w-full pl-11 pr-4 py-4 rounded-full bg-white text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 ring-[hsl(var(--primary-green))]"
                  />
                </div>
                <Link
                  to={`/places${search ? `?q=${encodeURIComponent(search)}` : ""}`}
                  data-testid="hero-explore-btn"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[hsl(var(--primary-green))] text-white font-semibold hover:bg-[hsl(var(--primary-green-hover))] transition-all hover:scale-[1.02]"
                >
                  {t.hero.explore} <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories - Bento */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{t.sections.categories}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 auto-rows-[180px] md:auto-rows-[220px]">
          {categories.map((c, idx) => {
            const isWide = idx === 0 || idx === 5;
            const isTall = idx === 2;
            return (
              <Link
                key={c.id}
                to={`/places?category=${c.id}`}
                data-testid={`category-card-${c.type}`}
                className={`group relative overflow-hidden rounded-2xl border border-border ${isWide ? "md:col-span-2" : ""} ${isTall ? "md:row-span-2" : ""}`}
              >
                <img src={typeImageMap[c.type] || heroImg} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 hero-scrim" />
                <div className="relative h-full p-5 md:p-6 flex flex-col justify-between">
                  <div className={`w-11 h-11 rounded-xl ${c.color === "red" ? "bg-[hsl(var(--primary-red))]" : "bg-[hsl(var(--primary-green))]"} text-white flex items-center justify-center`}>
                    {TYPE_ICONS[c.type] || <MapPin className="w-6 h-6" />}
                  </div>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-semibold text-white tracking-tight">{tx(c.name)}</h3>
                    <span className="mt-1 text-xs text-white/80 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      {t.common.viewAll} <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured places */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16 md:pb-24">
          <div className="flex items-end justify-between mb-2">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{t.sections.featured}</h2>
              <p className="text-muted-foreground mt-2">{t.sections.featuredSub}</p>
            </div>
            <Link to="/places" className="hidden md:inline-flex text-sm font-semibold text-[hsl(var(--primary-green))] hover:gap-2 items-center gap-1 transition-all">
              {t.common.viewAll} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {featured.slice(0, 6).map(p => <PlaceCard key={p.id} place={p} />)}
          </div>
        </section>
      )}

      {/* Map preview */}
      <section className="bg-[hsl(var(--accent))] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--primary-green))] text-white text-xs font-semibold uppercase">
                <MapIcon className="w-3.5 h-3.5" /> Map
              </span>
              <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight">{t.sections.mapTitle}</h2>
              <p className="mt-4 text-muted-foreground text-lg max-w-md">{t.sections.mapSub}</p>
              <Link to="/map" data-testid="open-map-btn" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--primary-green))] text-white font-semibold hover:bg-[hsl(var(--primary-green-hover))] transition">
                {t.nav.map} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-border shadow-xl bg-white">
              <img src={mapPreviewImg} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
